import clsx from 'clsx';

import { tg } from '../../lib/tg';

interface Option<T extends string | number> {
  value: T;
  label: string;
  count?: number;
}

interface Props<T extends string | number> {
  options: ReadonlyArray<Option<T>>;
  value: T;
  onChange: (v: T) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Stretch every option equally to fill the parent. */
  fullWidth?: boolean;
  /** Block all clicks (e.g. while a callback is awaiting an in-flight
   *  mutation). Buttons still render, just don't react. */
  disabled?: boolean;
}

/**
 * Segmented control — 2 to 4 options. Underline-style active state,
 * not pill — fits the editorial tone better.
 *
 * `lg` size pairs with `fullWidth` for the page-level Wallet tabs:
 * 48px hit area, equal-width slots, more breathing room — addresses
 * the "tabs feel glued together on the left" feedback.
 */
export function TabBar<T extends string | number>({
  options,
  value,
  onChange,
  className,
  size = 'md',
  fullWidth = false,
  disabled = false,
}: Props<T>) {
  return (
    <div
      role="tablist"
      className={clsx(
        'items-center rounded-pb-md border border-pb-border-soft bg-pb-paper2 p-1',
        fullWidth ? 'flex w-full gap-1' : 'inline-flex gap-1',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            role="tab"
            aria-selected={active}
            disabled={disabled}
            onClick={() => {
              tg.hapticLight();
              onChange(opt.value);
            }}
            className={clsx(
              'pb-focus-ring rounded-pb-sm font-medium transition-all',
              size === 'sm' && 'min-h-0 px-2.5 py-1 text-[12px]',
              size === 'md' && 'px-3 py-1.5 text-[13px]',
              size === 'lg' && 'min-h-[44px] px-5 py-2.5 text-[14px]',
              fullWidth && 'flex-1',
              disabled && 'cursor-not-allowed opacity-60',
              // Active state needs to be visibly distinct from the
              // container's `bg-pb-paper2`. Earlier we used `bg-tg-bg`
              // which on light theme resolved to nearly the same warm
              // cream as the container — the user couldn't tell which
              // tab was selected. Accent-tinted background reads
              // clearly in both themes.
              active
                ? 'bg-pb-accent-soft text-pb-accent shadow-pb-sm'
                : 'text-tg-hint hover:text-tg-text',
            )}
          >
            {opt.label}
            {opt.count !== undefined ? (
              <span className="ml-1.5 text-tg-hint">{opt.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
